import type { ReactElement } from 'react';
import { UI_DEFAULTS } from '@gr/core/platform/config/ui-config';
import type { PublicationDto } from '@gr/core/distribution/publishing/dto';
import { Link, useT } from '../../../../app/di';
import { formatDateTime } from '../../../../shared/format';
import { Badge } from '../../../../ui-kit/Badge/Badge';
import { Card } from '../../../../ui-kit/Card/Card';
import { overdueOf } from '../model/overdue';
import styles from './DueList.module.scss';

export interface DueListProps {
  blogId: string;
  timezone: string;
  publications: readonly PublicationDto[];
  /** Текущее время, мс — от него считается опоздание. */
  now: number;
  /** Опоздание меньше стольких минут — «вовремя» (ui.publishing.onTimeMinutes). */
  onTimeMinutes?: number;
}

/** PUB-UC-1: публикации, время которых наступило. */
export function DueList({ blogId, timezone, publications, now, onTimeMinutes = UI_DEFAULTS.publishing.onTimeMinutes }: DueListProps): ReactElement {
  const { t, locale } = useT();
  const overdueText = (plannedAt: string | null): string => {
    if (!plannedAt) return '';
    const overdue = overdueOf(plannedAt, now, onTimeMinutes);
    if (overdue.onTime) return t('publishing.due.onTime');
    const time = new Intl.NumberFormat(locale, { style: 'unit', unit: overdue.unit, unitDisplay: 'short' }).format(overdue.value);
    return t('publishing.due.overdue', { time });
  };
  return (
    <ul className={styles.list} data-testid="due-list">
      {publications.map((publication) => (
        <li key={publication.id}>
          <Card
            title={publication.postTitle}
            actions={
              <Link to="publication" params={{ blogId, publicationId: publication.id }}>
                {t('publishing.due.open')}
              </Link>
            }
          >
            <div className="u-row">
              <strong>{publication.channelName}</strong>
              <span>{formatDateTime(publication.plannedAt, locale, timezone)}</span>
              <Badge tone="warning">{overdueText(publication.plannedAt)}</Badge>
            </div>
          </Card>
        </li>
      ))}
    </ul>
  );
}
